import { Link, Navigate } from "react-router-dom";
import { useClients, useUnreadMessages, FamilyClientListItem } from "../lib/queries";
import { LoadingState, SkeletonList, ErrorState } from "../components/Loading";
import { PageShell } from "../components/PageShell";

/**
 * Messaging the coach, reached from the family level.
 *
 * "Has the coach said anything I need to act on?" is one of the four things a parent ever does here, and
 * it had no home in the navigation at all — the only route in was a "Messages" button on an athlete
 * card, which at phone width was the button hanging off the right edge of the screen (UX review F37,
 * and F19 for the button). It is also where the app sends people for anything it won't let them do
 * themselves, so "message your coach" pointed at a control they could not find.
 *
 * One athlete goes straight through, because a chooser between one option is a page that exists to be
 * clicked past. Several get the list, since the group thread is per-athlete even though the private one
 * covers the whole family.
 */
export function FamilyMessages() {
  const clientsQuery = useClients<FamilyClientListItem[]>();
  const unread = useUnreadMessages();
  const unreadClientIds = new Set(unread.data?.client_ids ?? []);
  const clients = clientsQuery.data;

  if (clients?.length === 1) {
    return <Navigate to={`/family/clients/${clients[0].id}/messages`} replace />;
  }

  return (
    <PageShell title="Messages">
      {clientsQuery.isPending && (
        <>
          <LoadingState label="Loading your conversations…" />
          <SkeletonList rows={2} />
        </>
      )}
      {clientsQuery.isError && <ErrorState error={clientsQuery.error} onRetry={() => clientsQuery.refetch()} />}

      {clientsQuery.isSuccess && clients && clients.length === 0 && (
        <p className="empty-state">No athletes on your account yet.</p>
      )}

      {clients && clients.length > 1 && (
        <>
          <p className="form-hint">
            Your private conversation with the coach is the same one wherever you open it. The group
            conversation is per athlete.
          </p>
          <ul className="client-list">
            {clients.map((client) => (
              <li key={client.id} className="client-card plan-card">
                <div className="plan-card-main">
                  <span className="client-name">{client.name}</span>
                  <Link className="link-button" to={`/family/clients/${client.id}/messages`}>
                    Open
                    {unreadClientIds.has(client.id) && (
                      <span className="nav-unread-dot" aria-label="Unread messages" />
                    )}
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </PageShell>
  );
}
